import React, {createRef, MutableRefObject, useContext, useMemo, useState} from 'react';
import {ButtonLoader, ResendOtp} from '..';
import {Constants, Strings} from '../../constants';
import {getDisplayErrorMsg, isMobile, setCookie} from '../../utils';
import {AccountContext, AccountContextInterface} from '../../contexts';
import {ScreenState} from '../../types';
import AccountPhoneInputField from '../phone-input';
import {Account, AccountAuthDetails} from '../../models';

type Props = {
  account: Account | null | undefined;
  setAccount: (account: Account) => void;
  createRecco?: () => void;
  onVerified?: (payload: {account: Account; auth: AccountAuthDetails}) => void;
  displayError: string;
  setDisplayError: (message: string) => void;
  state: ScreenState;
  setState: (newState: ScreenState) => void;
  showNameField?: boolean;
  headingText?: string;
  descriptionText?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  phoneLabel?: string;
  sendOtpText?: string;
  verifyButtonText?: string;
  containerClassName?: string;
};

export const VerifyUser: React.FC<Props> = ({
  state,
  setState,
  account,
  setAccount,
  displayError,
  setDisplayError,
  createRecco,
  onVerified,
  showNameField = true,
  headingText,
  descriptionText,
  nameLabel,
  namePlaceholder,
  phoneLabel,
  sendOtpText,
  verifyButtonText,
  containerClassName,
}) => {
  const {accountService} = useContext<AccountContextInterface>(AccountContext);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [resendOtpCounter, setResendOtpCounter] = useState<number>(1);
  const [invalidPhoneNumberMsg, setInvalidPhoneNumberMsg] = useState<string>('');
  const [submitPhoneLoader, setSubmitPhoneLoader] = useState<boolean>(false);
  const [submitOtpLoader, setSubmitOtpLoader] = useState<boolean>(false);
  const [verifyEnabled, setVerifyEnabled] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumebr] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [OTP, setOTP] = useState<string[]>(new Array(5).fill(''));
  const [name, setName] = useState<string>('');
  const [isExistingUser, setIsExistingUser] = useState(false);
  const otp = (): string => OTP.join('');

  const otpTextInputRefs: MutableRefObject<HTMLInputElement | null>[] = useMemo(() => {
    const refs: MutableRefObject<HTMLInputElement | null>[] = [];
    for (let i = 0; i < OTP.length; i++) {
      refs[i] = createRef<HTMLInputElement>();
    }
    return refs;
  }, [OTP.length]);

  const handlePhoneInput = (value: any, data: any) => {
    setPhoneNumebr(value);
    setCountryCode(data.dialCode);
    setCookie('token', '', 1);
  };

  const sendOtp = async (account: Account): Promise<void> => {
    const {data, error} = await accountService.sendOtp(account.id);
    if (data && !error) {
      setState(ScreenState.SUCCESS_WITH_NO_DATA);
    } else {
      setDisplayError(getDisplayErrorMsg(error));
      setState(ScreenState.ERROR);
    }
  };

  const handleSubmitPhone = async (): Promise<void> => {
    if (phoneNumber === '' || phoneNumber === null || phoneNumber === undefined) {
      return;
    } else if (!new RegExp(/^[\s()+-]*([0-9][\s()+-]*){10,15}$/).test(phoneNumber)) {
      setInvalidPhoneNumberMsg('Please specify a valid mobile number with country code.');
      setState(ScreenState.ERROR);
      return;
    }
    setSubmitPhoneLoader(true);

    const {data: results, error: searchError} = await accountService.search([
      {
        phoneNumber: phoneNumber.replace(countryCode, ''),
        countryCode: `+${countryCode}`,
      },
    ]);

    if (searchError) {
      setDisplayError(getDisplayErrorMsg(searchError));
      setState(ScreenState.ERROR);
      return;
    }

    if (results?.[0]?.account?.id) {
      setAccount(results?.[0]?.account);
      if (showNameField && results?.[0]?.account.firstName) {
        setName(results?.[0]?.account.displayName);
        setIsExistingUser(true);
      }
      await sendOtp(results?.[0]?.account);
      setSubmitPhoneLoader(false);
      setOtpSent(true);
    } else {
      const {data: account, error: createError} = await accountService.create({
        phoneNumber: phoneNumber.replace(countryCode, ''),
        countryCode: `+${countryCode}`,
      });
      if (createError) {
        setDisplayError(getDisplayErrorMsg(createError));
        setState(ScreenState.ERROR);
        setSubmitPhoneLoader(false);
        return;
      }
      if (account?.id) {
        setAccount(account);
        await sendOtp(account);
        setSubmitPhoneLoader(false);
        setOtpSent(true);
      }
    }
  };

  const resendOtp = async (): Promise<void> => {
    setState(ScreenState.DEFAULT);
    const {data, error} = await accountService.sendOtp(account!!.id);
    if (data && !error) {
      setResendOtpCounter(r => r + 1);
      setOTP(OTP.fill(''));
      (otpTextInputRefs[0].current as any).focus();
      setState(ScreenState.DEFAULT);
    } else {
      if (error?.toLowerCase().includes('maximum')) {
        setResendOtpCounter(5);
        return;
      }
      setDisplayError(getDisplayErrorMsg(error));
      setState(ScreenState.ERROR);
    }
  };

  const focusPreviousInput = (e: React.KeyboardEvent<HTMLInputElement>, index: number): void => {
    if (e.nativeEvent.key === 'Backspace' && index && OTP[index] === '') {
      (otpTextInputRefs[index - 1].current as any).focus();
      OTP[index - 1] = '';
      setOTP([...OTP]);
    }
  };

  const onOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setDisplayError('');
    const code = /(\d{5})/g.exec(e.target.value)?.[1] || '';
    if (isNaN(+e.target.value)) {
      if (!code) {
        return;
      }
    }

    if (index === 0 && code.length === 5) {
      setOTP(code.split(''));
      (otpTextInputRefs[index].current as any).blur();
      return;
    }

    OTP[index] = e.target.value;
    setOTP([...OTP]);

    const notBlank = OTP[index] !== '';
    const lastDigit = index === otpTextInputRefs.length - 1;
    lastDigit && notBlank && (otpTextInputRefs[index].current as any).blur();
    otpTextInputRefs[index + 1] && notBlank && (otpTextInputRefs[index + 1].current as any).focus();
    checkVerifyEnabled();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    checkVerifyEnabled();
  };

  const checkVerifyEnabled = () => {
    const nameReady = !showNameField || name.length > 1;
    if (!OTP?.filter(digit => digit === '').length && nameReady) {
      setVerifyEnabled(true);
    } else {
      setVerifyEnabled(false);
    }
  };

  const handleVerify = async (): Promise<void> => {
    const code = otp();
    if (code.length !== 5) {
      return;
    }
    setSubmitOtpLoader(true);

    const {data: auth, error} = await accountService.verifyOtp(account!!.id, code);
    if (auth && !error) {
      setState(ScreenState.SUCCESS_WITH_DATA);
      setSubmitOtpLoader(false);
      if (auth.token) {
        setCookie('token', auth.token, 365);
      }
      !isExistingUser &&
        account &&
        showNameField &&
        accountService.updateAccount(account.id, {
          firstName: name,
        });
      setVerifyEnabled(false);
      if (account) {
        if (onVerified) {
          onVerified({account, auth});
        } else {
          createRecco?.();
        }
      }
    } else {
      setDisplayError(getDisplayErrorMsg(error));
      setVerifyEnabled(false);
      setState(ScreenState.ERROR);
      setSubmitOtpLoader(false);
      setOTP(new Array(Constants.OTP_CODE_LENGTH).fill(''));
      (otpTextInputRefs[0].current as any).focus();
    }
  };

  const hasError = state === ScreenState.ERROR;

  const wrapperClasses =
    containerClassName || 'mx-auto w-full max-w-md rounded-3xl border border-gray-100 bg-white/90 p-8 shadow-lg shadow-blue-50';

  return (
    <div className={wrapperClasses}>
      <p className="text-2xl font-semibold text-gray-900 text-center">{headingText || Strings.OTP_VERIFY_HEADING}</p>
      <p className="mt-2 text-sm text-gray-600 text-center">{descriptionText || Strings.RECCO_DETAILS_CLICKED_MESSAGE}</p>

      {showNameField && (
        <>
          <label className="mt-6 block mb-2 text-sm font-semibold text-gray-700">{nameLabel || Strings.NAME_LABEL}</label>
          <input
            onChange={e => handleNameChange(e.target.value)}
            placeholder={namePlaceholder || Strings.NAME_PLACEHOLDER}
            value={name}
            className="mb-4 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-2 ring-transparent transition focus:border-blue-500 focus:ring-blue-100"
          />
        </>
      )}

      <label className="block mb-2 text-sm font-semibold text-gray-700">{phoneLabel || Strings.PHONE_LABEL}</label>
      <div className="mb-4 w-full space-y-3">
        <AccountPhoneInputField
          errorClassName="account-field-icon"
          placeholder="xxxxxxxxxxxx"
          label="Phone"
          name="phone"
          value={phoneNumber}
          invalid={false}
          invalidMsg={invalidPhoneNumberMsg}
          disabled={submitPhoneLoader}
          handleInputChange={handlePhoneInput}
        />
        <button
          className={`w-full rounded-full border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70 ${
            otpSent ? 'hidden' : ''
          }`}
          disabled={submitPhoneLoader}
          onClick={handleSubmitPhone}>
          {submitPhoneLoader ? <ButtonLoader /> : sendOtpText || 'Send OTP'}
        </button>
      </div>

      <div className={`flex flex-col ${!otpSent ? 'hidden' : ''}`}>
        <div className="flex mb-3">
          <div className="flex w-full justify-between">
            {OTP.map((digit, index) => (
              <input
                key={index}
                ref={ref => (otpTextInputRefs[index].current = ref)}
                onChange={e => onOtpChange(e, index)}
                onKeyUp={e => focusPreviousInput(e, index)}
                placeholder="-"
                value={OTP[index]}
                autoFocus={index === 0}
                className="w-1/5 rounded-xl border border-gray-200 px-3 py-3 text-center text-lg font-semibold text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            ))}
          </div>
        </div>
        {resendOtpCounter <= 5 ? otpSent && <ResendOtp onClick={resendOtp} /> : <div>OTP limit reached, Please try again later</div>}
      </div>

      {hasError && <span className="text-red-500 block text-center mt-3">{displayError}</span>}

      <button
        className={`mt-6 w-full rounded-full px-4 py-3 text-sm font-semibold text-white transition ${
          verifyEnabled ? 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-100' : 'bg-gray-300 cursor-not-allowed'
        } ${submitOtpLoader ? 'cursor-wait opacity-80' : ''}`}
        disabled={!verifyEnabled || submitOtpLoader}
        onClick={handleVerify}>
        {submitOtpLoader ? <ButtonLoader /> : verifyButtonText || Strings.VERIFY}
      </button>
    </div>
  );
};
